import Avatar from './Avatar';

const DEFAULT_PRIMARY = '#9b5de5';
const DEFAULT_ACCENT = '#7c3aed';
const STATUS_LABEL = { online: 'Online', away: 'Away', dnd: 'Do not disturb', offline: 'Offline' };
const STATUS_DOT = { online: 'bg-green', away: 'bg-amber', dnd: 'bg-red' };

// Card colors: a vertical gradient from primary to accent, darkened so text stays readable
export function cardBackground(profile) {
  if (!profile?.card_color_primary && !profile?.card_color_accent) return undefined;
  const a = profile.card_color_primary || DEFAULT_PRIMARY;
  const b = profile.card_color_accent || profile.card_color_primary || DEFAULT_ACCENT;
  return `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), linear-gradient(180deg, ${a}, ${b})`;
}

// Banner image, or the card color gradient, or the BLOBL gradient
export function ProfileBanner({ profile, height = 96, className = '', onClick, children }) {
  const a = profile?.card_color_primary || DEFAULT_PRIMARY;
  const b = profile?.card_color_accent || profile?.card_color_primary || DEFAULT_ACCENT;
  return (
    <div
      onClick={onClick}
      className={`relative shrink-0 bg-cover bg-center ${onClick ? 'cursor-zoom-in' : ''} ${className}`}
      style={{
        height,
        backgroundImage: profile?.banner ? `url(${profile.banner})` : `linear-gradient(135deg, ${a}, ${b})`,
      }}
    >
      {children}
    </div>
  );
}

// Shared profile card: chat side panel, profile page and edit preview.
// The surrounding container sets the card color background (cardBackground), so it fills the whole area.
// renderBio lets the profile page turn @mentions into links.
export default function ProfileCard({
  profile, nickname, status, bannerHeight = 96, avatarSize = 80, bannerTopInset = false,
  onAvatarClick, onBannerClick, renderBio, actions, topRight, nameAction, children,
}) {
  const name = nickname || profile.display_name || profile.username;
  return (
    <div className="flex flex-col">
      <ProfileBanner
        profile={profile}
        height={bannerTopInset ? `calc(${bannerHeight}px + var(--safe-top))` : bannerHeight}
        onClick={onBannerClick}
      >
        {topRight}
      </ProfileBanner>
      <div className="px-4 pb-4">
        <div className="flex items-end justify-between">
          <button
            type="button"
            onClick={onAvatarClick}
            disabled={!onAvatarClick}
            className={`rounded-full border-[6px] border-solid border-surface bg-surface p-0 ${onAvatarClick ? 'cursor-zoom-in' : 'cursor-default'}`}
            style={{ marginTop: -(avatarSize / 2 + 6), borderColor: cardBackground(profile) ? 'rgba(0,0,0,0.6)' : undefined }}
          >
            <Avatar src={profile.avatar} size={avatarSize} border="none" status={status} />
          </button>
          {actions && <div className="flex gap-2 pb-1">{actions}</div>}
        </div>

        <div className="relative mt-3 rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.55)' }}>
          {nameAction && <div className="absolute right-1.5 top-1.5">{nameAction}</div>}
          <div className={`break-words font-display text-xl font-extrabold text-text ${nameAction ? 'pr-8' : ''}`}>{name}</div>
          <div className="text-sm text-text-muted">@{profile.username}</div>
          {nickname && <div className="mt-1 text-xs text-text-dim">Nickname set by you</div>}
          {status && (
            <div className="mt-2 flex items-center gap-2 text-xs text-text-muted">
              <span className={`h-2 w-2 rounded-full ${STATUS_DOT[status] || 'bg-text-dim'}`} />
              {STATUS_LABEL[status] || 'Offline'}
            </div>
          )}

          {profile.bio && (
            <>
              <div className="my-3 h-px bg-border" />
              <div className="mb-1 text-[11px] font-extrabold uppercase tracking-wider text-text-dim">About me</div>
              <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-text">
                {renderBio ? renderBio(profile.bio) : profile.bio}
              </div>
            </>
          )}

          {profile.created_at && (
            <>
              <div className="my-3 h-px bg-border" />
              <div className="mb-1 text-[11px] font-extrabold uppercase tracking-wider text-text-dim">Member since</div>
              <div className="text-sm text-text">
                {new Date(profile.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
